import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAppSelector } from "../../../../app/hooks";
import { selectIsKlearlyAdmin } from "../../../access-control/accountSlice";
import { Select, Spin, Tooltip } from "antd";
import styles from "./PartnersSelector.module.css";
import { getPartnersFromServer } from "../../../partners/partnersApi";
import { LoadingOutlined, SearchOutlined } from "@ant-design/icons";
import debounce from "lodash/debounce";

type PartnerOption = { label: string; value: string };

export interface IPropsPartnerSelector {
  onChange: (partnerId: string) => any;
  allowClear?: boolean;
  addDefaultValue?: boolean;
  value?: string;
  disabled?: boolean;
  disabledHint?: string;
  width?: string;
}

const PAGE_SIZE = 20;

const toOptions = (
  partners: { _id: string; name: string }[]
): PartnerOption[] =>
  partners.map((p) => ({
    label: p.name,
    value: p._id,
  }));

// GET /partners has no sort, so a page arrives in insertion order.
const byName = (a: PartnerOption, b: PartnerOption): number =>
  a.label.localeCompare(b.label, undefined, { sensitivity: "base" });

const withSelected = (
  opts: PartnerOption[],
  selected: PartnerOption | undefined
): PartnerOption[] =>
  selected && !opts.some((o) => o.value === selected.value)
    ? [selected, ...opts]
    : opts;

export default function PartnerSelector({
  onChange,
  allowClear = true,
  addDefaultValue,
  value,
  disabled,
  disabledHint,
  width = "260px",
}: IPropsPartnerSelector) {
  const isKlearlyAdmin = useAppSelector(selectIsKlearlyAdmin);
  const [options, setOptions] = useState<PartnerOption[]>([]);
  const [loading, setLoading] = useState(false);
  // Partners matching the current query that the page left out, so a list that
  // cannot hold them all says so rather than reading as the whole set.
  const [omittedCount, setOmittedCount] = useState(0);
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const selectedRef = useRef<PartnerOption | undefined>();
  const fetchIdRef = useRef(0);
  const openRef = useRef(false);

  if (!value) selectedRef.current = undefined;
  else if (selectedRef.current?.value !== value) {
    const found = optionsRef.current.find((o) => o.value === value);
    if (found) selectedRef.current = found;
  }

  const fetchOptions = async (search: string) => {
    const requestId = ++fetchIdRef.current;
    setLoading(true);
    try {
      const resp = await getPartnersFromServer({
        name: search,
        currentPage: 1,
        pageSize: PAGE_SIZE,
      });
      if (requestId !== fetchIdRef.current) return;
      const page = toOptions(resp?.data || []).sort(byName);
      setOptions(withSelected(page, selectedRef.current));
      setOmittedCount(
        Math.max((resp?.totalCount ?? page.length) - page.length, 0)
      );
    } catch {
      if (requestId !== fetchIdRef.current) return;
    } finally {
      if (requestId === fetchIdRef.current) setLoading(false);
    }
  };
  const debouncedFetch = useMemo(() => debounce(fetchOptions, 400), []);

  useEffect(() => () => debouncedFetch.cancel(), [debouncedFetch]);

  // Closed select needs the current partner in `options` so its label renders.
  useEffect(() => {
    let cancelled = false;
    const ensureSelectedOption = async () => {
      if (!value) return;
      const existing = optionsRef.current.find((o) => o.value === value);
      if (existing) {
        selectedRef.current = existing;
        return;
      }
      try {
        const resp = await getPartnersFromServer({ partnerId: value });
        const p = resp?.data?.[0];
        if (cancelled || !p) return;
        const selected = { label: p.name, value: p._id };
        selectedRef.current = selected;
        setOptions((prev) => withSelected(prev, selected));
      } catch {}
    };
    ensureSelectedOption();
    return () => {
      cancelled = true;
    };
  }, [value]);
  const select = (
    <Select
      showSearch
      filterOption={false}
      value={value}
      options={options}
      placeholder="Partner Name"
      allowClear={allowClear}
      className={styles.searchInput}
      style={{ width }}
      suffixIcon={loading ? <LoadingOutlined spin /> : <SearchOutlined />}
      disabled={disabled}
      onSearch={(val) => {
        debouncedFetch.cancel();
        if (!val) {
          // rc-select empties the box on close and on clear as well; only an
          // open dropdown needs the unfiltered page back.
          if (openRef.current) fetchOptions("");
          return;
        }
        debouncedFetch(val);
      }}
      onOpenChange={(open) => {
        openRef.current = open;
        debouncedFetch.cancel();
        if (!open) return;
        // Without this the last search's rows stand until the page lands, so
        // the list the operator is reading changes under them.
        setOptions(withSelected([], selectedRef.current));
        setOmittedCount(0);
        // A selection already fills `options`, so open must still load the page.
        fetchOptions("");
      }}
      loading={loading}
      popupRender={(menu) => (
        <>
          {menu}
          {omittedCount > 0 && (
            <div
              data-testid="partner-selector-omitted"
              style={{
                padding: "6px 12px",
                borderTop: "1px solid #f0f0f0",
                color: "rgba(0,0,0,0.45)",
                fontSize: 12,
              }}
            >
              {omittedCount} more partners not shown — type to search
            </div>
          )}
        </>
      )}
      notFoundContent={
        loading ? (
          <div style={{ textAlign: "center", padding: "8px 0" }}>
            <Spin size="small" />
          </div>
        ) : undefined
      }
      onChange={(newValue) => {
        onChange(newValue as string);
      }}
    />
  );

  return (
    <div>
      {isKlearlyAdmin &&
        (disabled && disabledHint ? (
          <Tooltip title={disabledHint}>
            <span
              title={disabledHint}
              style={{ display: "inline-block", width }}
            >
              {select}
            </span>
          </Tooltip>
        ) : (
          select
        ))}
    </div>
  );
}
